import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { axiosInstance } from "@hopper-b2b/api";
import { useExperiment } from "@hopper-b2b/experiments";
import {
  ClientName,
  GetWalletOffersResponseEnum,
  type WalletVouchersResponse,
} from "@hopper-b2b/types";
import { getEnvVariables, useEnableWallet } from "@hopper-b2b/utilities";
import {
  fetchWalletOffers,
  setSelectedWalletOffer,
  setWalletOffers,
  setWalletOffersFailed,
} from "../actions/actions";
import { getWalletBestOffers, getWalletOffers } from "../reducer";
import { mapBffOfferToWalletOffer } from "../utils/mapBffOfferToWalletOffer";

export const useWalletOffers = () => {
  const dispatch = useDispatch();
  const isWalletEnabled = useEnableWallet();
  const offers = useSelector(getWalletOffers);
  const bestOffer = useSelector(getWalletBestOffers);
  const [offersLoaded, setOffersLoaded] = useState(offers?.length > 0);
  const enableVouchers10 = useExperiment(
    "hotel-voucher-discount-2024-10-percent"
  );
  const enableVouchers20 = useExperiment(
    "hotel-voucher-discount-2024-20-percent"
  );

  // Lloyds has their own wallet offer hook
  const isLloydsClient = getEnvVariables("clientName") === ClientName.LLOYDS;
  const isNubankClient = getEnvVariables("clientName") === ClientName.NUBANK;

  const getNubankOffers = useCallback(async () => {
    try {
      dispatch(fetchWalletOffers());

      const res = await axiosInstance.get("/api/v0/wallet/offers");
      const offersData = res.data;

      if (!offersData?.offers?.length) {
        dispatch(setWalletOffersFailed());
        return;
      }

      const walletOffers = offersData.offers.map(mapBffOfferToWalletOffer);

      if (!walletOffers.length) {
        dispatch(setWalletOffersFailed());
        return;
      }

      dispatch(
        setWalletOffers({
          offers: walletOffers,
          bestOffer: walletOffers[0],
        })
      );
      dispatch(setSelectedWalletOffer(walletOffers[0]));
    } catch (e) {
      console.warn("wallet fetching error:", e);
      dispatch(setWalletOffersFailed());
    } finally {
      setOffersLoaded(true);
    }
  }, [dispatch]);

  const getOffers = useCallback(async () => {
    try {
      dispatch(fetchWalletOffers());

      const partnerExperiments = enableVouchers10
        ? "hotel-voucher-discount-2024-10-percent"
        : enableVouchers20
          ? "hotel-voucher-discount-2024-20-percent"
          : "";

      const req = {
        partnerExperiments,
      };

      const getOffersResponse: WalletVouchersResponse = (
        await axiosInstance.put("/api/v0/wallet/vouchers", req)
      ).data;
      if (
        getOffersResponse.GetVouchersResponse ===
        GetWalletOffersResponseEnum.Success
      ) {
        dispatch(
          setWalletOffers({
            offers: getOffersResponse.offers,
            bestOffer: getOffersResponse.offers[0],
          })
        );
        dispatch(setSelectedWalletOffer(getOffersResponse.offers[0]));
      } else {
        dispatch(setWalletOffersFailed());
      }
    } catch (e) {
      console.warn("wallet fetching error:", e);
      dispatch(setWalletOffersFailed());
    } finally {
      setOffersLoaded(true);
    }
  }, [dispatch, enableVouchers10, enableVouchers20]);

  useEffect(() => {
    if (isLloydsClient) return;

    // Nubank DMO offers are always fetched — they drive the offer banners
    // across all lodging screens and aren't gated by the wallet feature flag.
    if (isNubankClient) {
      getNubankOffers();
      return;
    }

    if (!isWalletEnabled) return;
    getOffers();
  }, [
    getOffers,
    getNubankOffers,
    isWalletEnabled,
    isLloydsClient,
    isNubankClient,
  ]);

  return {
    loading: !offersLoaded,
    offers: offers,
    bestOffer: bestOffer,
  };
};
